// @givmo/app-admin — the Settings tab REGISTRATION contract (EP-DES2 Wave 0).
//
// The admin settings shell is a shared surface: this program owns it, and Account Security, EPSM,
// and White-Label each add a tab to it later. Before Wave 0 the four tabs were hard-coded into
// `settings-page.tsx`, so every one of those programs would have had to edit the same JSX — three
// forks of one file. This module is the seam that prevents that: a tab DECLARES itself (id, label,
// order, permission gate, component) and the page renders whatever is registered.
//
// The published contract — stability guarantees, how to register, what may and may not change — is
// `agent_docs/admin-settings-tabs.md`. Read it before adding a tab; it is the law, this file is the
// mechanism.
//
// The registry is SPLIT so the contract is structurally safe, not just documented: this program's
// core tabs live in `CORE_SETTINGS_TABS` (pinned by EP's tests), other programs append to
// `EXTENSION_SETTINGS_TABS` (their own tests bind that), and `settingsTabRegistry()` composes both.
// A registrant therefore cannot break EP's pins by adding a tab — the failure mode a single shared
// array would have had.
//
// Three properties the page depends on and the tests pin:
//   - ORDER is `order` ascending over the AGGREGATE, ties broken by registration index (stable
//     sort), so a late registrant can slot between two existing tabs without renumbering them.
//   - PERMISSION is an optional predicate over the same context the component receives. Omitted =
//     always visible, which is what all three core tabs do (their internal Owner gates are unchanged
//     and still live inside the tab bodies — this gate is about whether the TAB EXISTS at all).
//   - UNKNOWN IDS ARE TOLERATED, never thrown on: an id that is not registered (a stale deep link,
//     a tab hidden by permission after being selected) resolves to the first visible tab.

import { useSyncExternalStore, type ComponentType } from 'react';
import type { CompanyAdminMembershipType, CompanyDetailedOut } from '@givmo/api-client';
import { SettingsPrivacyTab } from './settings-privacy-tab';
import { SettingsAccountTab } from './settings-account-tab';
import { SettingsPaymentMethodsTab } from './settings-payment-methods-tab';
import { GIVING_CARD_SETTINGS_TAB } from '../giving-card/settings-giving-card-tab';
import { SECURITY_SETTINGS_TAB } from '../security/settings-security-tab';

/**
 * What a permission predicate sees. It is evaluated to build the TAB STRIP, which renders while the
 * company is still loading — so `company` is nullable here and a gate must decide what a not-yet-
 * known value means for it. Same for `role`: returning `false` on `null` hides the tab until the
 * value lands, which is usually the safe choice for a privileged tab.
 */
export interface SettingsTabPermissionContext {
  company: CompanyDetailedOut | null;
  role: CompanyAdminMembershipType | null;
}

/**
 * What a registered tab's COMPONENT receives. Panels only ever render once the company has loaded,
 * so `company` is non-null here. Widening this is ADDITIVE ONLY: a tab compiled against an older
 * shape keeps working because it destructures only the fields it declared.
 */
export interface SettingsTabContext extends SettingsTabPermissionContext {
  /** The company, loaded ONCE by the page (`GET /admin/company`) and threaded into every tab. */
  company: CompanyDetailedOut;
}

/** One registered tab. */
export interface SettingsTabDefinition {
  /** Stable, kebab-case, and part of the `data-testid` contract — never rename a shipped id. */
  id: string;
  /** The human-facing tab label. */
  label: string;
  /** Sort key, ascending. Core tabs use decades so a registrant can slot between them. */
  order: number;
  /** Optional visibility gate, evaluated against the possibly-unloaded context. Omitted = always visible. */
  permission?: (context: SettingsTabPermissionContext) => boolean;
  /** The panel body. Receives the full {@link SettingsTabContext}. */
  component: ComponentType<SettingsTabContext>;
}

/**
 * The tab strip's `data-testid` for a tab id (`settings-tab-privacy`). The DERIVATION is the
 * contract — always compose through this rather than hand-writing the string. All three core ids have
 * live Playwright selectors (`account`, `privacy`, `payments`).
 */
export function settingsTabTestId(id: string): string {
  return `settings-tab-${id}`;
}

/** The panel's `data-testid` for a tab id (`settings-panel-privacy`). Same derivation contract. */
export function settingsPanelTestId(id: string): string {
  return `settings-panel-${id}`;
}

/**
 * The three tabs THIS program owns, in their shipped order. Their ids/labels/testids are a contract,
 * not a default: `givmo-e2e-test` Playwright selects all three (`account`, `privacy`, `payments`),
 * the per-tab RTL suites cover them, and the registry tests pin this array exactly.
 *
 * Match Settings (order 10) RETIRED at EP-MNT2: the match policy is its own page under Giving,
 * `/match-program`, and `/settings?tab=match` redirects there (the route, not this registry). The
 * other orders did not move, so no registrant's slot did either.
 *
 * ⚠ Registrants: do NOT append here — that would break the Employer Portal's pins. Append to
 * {@link EXTENSION_SETTINGS_TABS} instead.
 *
 * Orders are spaced by 10 deliberately: Account Security, EPSM, and White-Label register at 50+
 * (appended), but a program that must sit between two core tabs can take 25/35 without this
 * array changing.
 */
export const CORE_SETTINGS_TABS: readonly SettingsTabDefinition[] = [
  { id: 'privacy', label: 'Privacy Settings', order: 20, component: SettingsPrivacyTab },
  { id: 'account', label: 'Account info', order: 30, component: SettingsAccountTab },
  { id: 'payments', label: 'Payment Methods', order: 40, component: SettingsPaymentMethodsTab },
];

/**
 * Tabs registered by OTHER programs — Account Security, EPSM, White-Label, and the Connect Widgets
 * program's giving card. **This is where a registrant appends.**
 *
 * Splitting core from extensions is what makes the contract structurally safe rather than
 * documentation-safe: the Employer Portal's pins bind {@link CORE_SETTINGS_TABS}, so a registrant
 * cannot break them by adding a tab, and a registrant's own tests bind this array without having to
 * know what the core tabs are. Ordering across the two is a single sort over the aggregate, so an
 * extension still interleaves freely (take 25/35 to sit between core tabs).
 *
 * ⚠ EVERY ENTRY HERE IS STILL PERMISSION-GATED, so a registration is not an appearance: the
 * giving-card tab (CW-WEB4) carries its own `VITE_COMPANY_GIVING_CARD_ENABLED` + Owner predicate and
 * is invisible — leaving the strip byte-identical to the core three — until both hold.
 */
export const EXTENSION_SETTINGS_TABS: readonly SettingsTabDefinition[] = [
  GIVING_CARD_SETTINGS_TAB,
  SECURITY_SETTINGS_TAB,
];

/**
 * The registry the page renders from: core followed by extensions. Composed per call (not frozen at
 * module load) so a registrant that appends at runtime rather than in source is still picked up.
 */
export function settingsTabRegistry(): readonly SettingsTabDefinition[] {
  return [...CORE_SETTINGS_TABS, ...EXTENSION_SETTINGS_TABS];
}

/**
 * The tabs actually rendered for this caller: permission-filtered, then ordered.
 *
 * The sort is STABLE (`Array.prototype.sort` is spec-stable), so equal `order` values keep their
 * registration sequence rather than swapping unpredictably between renders.
 */
export function resolveSettingsTabs(
  context: SettingsTabPermissionContext,
  registry: readonly SettingsTabDefinition[] = settingsTabRegistry(),
): SettingsTabDefinition[] {
  return registry
    .filter((tab) => tab.permission?.(context) ?? true)
    .slice()
    .sort((a, b) => a.order - b.order);
}

// ── Asynchronous gates (AS-WEB4) ────────────────────────────────────────────────────────────────
//
// A `permission` predicate is SYNCHRONOUS, but a registrant whose dark gate is the server's own
// answer only learns the verdict after a request settles. Without a way to say "ask again", such a
// tab can never appear: `settings-page.tsx` renders the strip from `resolveSettingsTabs`, and it can
// render exactly ONCE — the role comes from the query key the app shell already mounted
// (`app-shell.tsx`), and `GET /admin/company` has a 30s `staleTime`, so arriving from the dashboard
// or the employees page serves both from cache with no pending→success transition to re-render on.
// A probe that settles a moment later would then reveal nothing until the Owner clicked another tab.
//
// So the registry carries a tiny notification store. A registrant calls
// `notifySettingsTabGatesChanged()` when its asynchronous gate settles; the page subscribes ONCE via
// `useSettingsTabGates()` and re-resolves. This is the additive capability the contract asks for
// instead of a registrant editing the page (`agent_docs/admin-settings-tabs.md`), and it is generic:
// nothing here knows which tab is asking, and a gate that is purely synchronous never calls it.

const gateListeners = new Set<() => void>();
let gateVersion = 0;

/**
 * Tell the settings page that an asynchronous gate settled, so the strip re-resolves.
 *
 * Safe to call at any time, including from outside React and before the page has mounted (the
 * listener set is simply empty). Idempotent in effect: the page re-runs the same pure
 * `resolveSettingsTabs`, so an extra call costs a render and changes nothing else.
 */
export function notifySettingsTabGatesChanged(): void {
  gateVersion += 1;
  // ⚠ Iterate a COPY. A `Set` iterated live also visits entries ADDED during the pass, so a listener
  // that subscribes another — a second async-gated tab mounting because the first one settled —
  // would have that new listener invoked by the notification it did not exist for. Harmless with one
  // registrant, confusing with two, and free to prevent.
  for (const listener of [...gateListeners]) listener();
}

/**
 * Listen for gate changes outside React. `useSettingsTabGates` is the ordinary door; this one exists
 * because the store is part of the registry's published contract and has to be observable without a
 * renderer. Returns the unsubscribe.
 */
export function subscribeSettingsTabGates(listener: () => void): () => void {
  gateListeners.add(listener);
  return () => {
    gateListeners.delete(listener);
  };
}

/**
 * Subscribe the settings page to asynchronous gate changes. Call it ONCE, unconditionally — it is a
 * plain hook, so it must not sit behind a condition or inside a loop over the registry.
 *
 * The returned version number is not meant to be read; it exists so React has a value to compare.
 * `getServerSnapshot` is the same reader, which is what keeps this safe if the admin app is ever
 * server-rendered.
 */
export function useSettingsTabGates(): number {
  return useSyncExternalStore(
    subscribeSettingsTabGates,
    () => gateVersion,
    () => gateVersion,
  );
}

/**
 * Pick the tab to show. Tolerates an unknown/hidden `requested` id by falling back to the first
 * visible tab — a stale deep link or a tab that a permission change just hid must never render an
 * empty settings surface or throw. Returns `null` only when NOTHING is visible.
 */
export function resolveActiveTabId(
  requested: string | null | undefined,
  tabs: readonly SettingsTabDefinition[],
): string | null {
  const first = tabs[0];
  if (first === undefined) return null;
  return tabs.some((tab) => tab.id === requested) ? (requested as string) : first.id;
}
